/**
 * @file sampling-sketch-core.ts
 * @description 採樣位置示意圖畫板（SamplingSketchPad）的純邏輯核心。
 *
 * 與 canvas / DOM 完全解耦的純函數集合，便於 vitest 單測：
 * - 撤銷快照棧：pushSnapshot / popSnapshot（不可變操作、容量上限淘汰最舊）
 * - 底圖排版：computeContainRect（等比縮放置中，contain 語意）
 * - 座標映射：mapPointerToCanvas（CSS 顯示尺寸 → canvas 內部像素座標）
 */

/** 撤銷快照棧的預設容量上限（超出時淘汰最舊快照，防記憶體無限增長） */
export const UNDO_STACK_LIMIT = 30;

/**
 * 推入一個撤銷快照，返回新棧（不可變，不修改入參）。
 *
 * 超出 `max` 容量時丟棄最舊的快照（棧底），保留最近 max 筆。
 *
 * @param stack 現有快照棧（棧頂在陣列末端）
 * @param snap  新快照（畫板場景下為 ImageData，泛型以便單測用簡單值）
 * @param max   容量上限，預設 UNDO_STACK_LIMIT；不足 1 時按 1 處理
 * @returns 推入後的新棧
 */
export function pushSnapshot<T>(stack: readonly T[], snap: T, max = UNDO_STACK_LIMIT): T[] {
  const limit = Math.max(1, max);
  const next = [...stack, snap];
  // 超限時從棧底（最舊）開始丟棄
  return next.length > limit ? next.slice(next.length - limit) : next;
}

/**
 * 彈出棧頂撤銷快照，返回 { top, rest }（不可變，不修改入參）。
 *
 * @param stack 現有快照棧
 * @returns top：棧頂快照（空棧時為 undefined）；rest：彈出後的新棧
 */
export function popSnapshot<T>(stack: readonly T[]): { top: T | undefined; rest: T[] } {
  if (stack.length === 0) return { top: undefined, rest: [] };
  return { top: stack[stack.length - 1], rest: stack.slice(0, -1) };
}

/** 矩形區域（canvas 座標系） */
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * 計算底圖在畫布內「等比縮放置中」（CSS object-fit: contain 語意）的繪製矩形。
 *
 * 用於上傳底圖後鋪滿畫布背景：不裁切、不變形，長邊貼齊畫布、短邊居中留白。
 *
 * @param imgW    圖片原始寬（px）
 * @param imgH    圖片原始高（px）
 * @param canvasW 畫布寬（px）
 * @param canvasH 畫布高（px）
 * @returns 繪製矩形；任一入參 <= 0 時返回整個畫布（防禦性回落）
 */
export function computeContainRect(
  imgW: number,
  imgH: number,
  canvasW: number,
  canvasH: number,
): Rect {
  if (imgW <= 0 || imgH <= 0 || canvasW <= 0 || canvasH <= 0) {
    return { x: 0, y: 0, width: canvasW, height: canvasH };
  }
  // contain：取兩個維度中較小的縮放比，保證圖片完整落在畫布內
  const scale = Math.min(canvasW / imgW, canvasH / imgH);
  const width = imgW * scale;
  const height = imgH * scale;
  return {
    x: (canvasW - width) / 2,
    y: (canvasH - height) / 2,
    width,
    height,
  };
}

/** DOMRect 的最小子集（getBoundingClientRect 的必要欄位，便於單測構造） */
export interface DisplayRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * 把指標事件的 client 座標映射為 canvas 內部像素座標。
 *
 * canvas 的內部解析度（width/height 屬性）與 CSS 顯示尺寸（style 寬高）
 * 通常不一致（畫板固定內部解析度、CSS 寬 100% 自適應），需按比例換算。
 *
 * @param clientX 指標事件 clientX
 * @param clientY 指標事件 clientY
 * @param rect    canvas.getBoundingClientRect() 的顯示矩形
 * @param canvasW canvas 內部像素寬（canvas.width）
 * @param canvasH canvas 內部像素高（canvas.height）
 * @returns canvas 內部座標；顯示矩形寬高為 0 時返回 (0,0)（防除零）
 */
export function mapPointerToCanvas(
  clientX: number,
  clientY: number,
  rect: DisplayRect,
  canvasW: number,
  canvasH: number,
): { x: number; y: number } {
  if (rect.width <= 0 || rect.height <= 0) return { x: 0, y: 0 };
  return {
    x: ((clientX - rect.left) / rect.width) * canvasW,
    y: ((clientY - rect.top) / rect.height) * canvasH,
  };
}
